import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { NotebookPen, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { celebrateFrom } from "@/lib/celebrate";
import CollapsibleCard from "@/components/CollapsibleCard";
import BioEditor, { overLimit } from "@/components/bios/BioEditor";
import BioDueChip from "@/components/bios/BioDueChip";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/date";

// Team member Profile: write / update own bio. Hidden when Bios are off.
export default function MemberBioCard() {
  const { search } = useLocation();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/bios/my").then(({ data: d }) => { setData(d); setText(d.bio || ""); }).catch(() => {}); }, []);
  useEffect(() => {
    if (data?.enabled && search.includes("section=bio")) document.querySelector('[data-testid="member-bio-card"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [data?.enabled, search]);
  if (!data?.enabled) return null;

  const dirty = text.trim() !== (data.bio || "").trim();
  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put("/bios/my", { bio: text });
      setData(d); setText(d.bio || "");
      toast.success("Bio saved");
      celebrateFrom(d);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <CollapsibleCard title={<span className="flex items-center gap-2">Bio <span className="rounded-full bg-slate-100 px-2 py-0.5 font-sans text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-testid="member-bio-optional">Optional</span></span>}
      icon={NotebookPen} testId="member-bio-card" defaultOpen={search.includes("section=bio")}>
      <p className="mb-4 text-sm text-slate-500">A short bio your director can use in programs and playbills. Add or update it any time.</p>
      {data.deadline && data.nudged && <div className="mb-3"><BioDueChip deadline={data.deadline} testId="member-bio-due" /></div>}
      <BioEditor value={text} onChange={setText} program={data.program} testId="member-bio-textarea" />
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={save} disabled={saving || !dirty || overLimit(text)} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="member-bio-save">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {data.bio ? "Update bio" : "Save bio"}
        </Button>
        {data.updated_at && (
          <span className="text-xs text-slate-400" data-testid="member-bio-updated">
            Last updated {formatDate(data.updated_at)}{data.edited_by_director && data.updated_by_name ? ` by ${data.updated_by_name}` : ""}
          </span>
        )}
      </div>
    </CollapsibleCard>
  );
}
